import React, { ChangeEvent, Component } from 'react';

import { Button, message, Tooltip } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import classNames from 'classnames';

import { Input } from '@/components/Common/UIKit';
import i18n from '@/content';

import TagsList from './TagsList';
import PolicySetting, { PolicyRowProps } from '../PolicySetting';
import { ListTypeInputs } from './interfaces';
import OPTIONS from './options';
import './index.scss';

interface PolicyTagInputRowProps extends PolicyRowProps<string[]> {
    listType: ListTypeInputs;
    hasError?: boolean;
    errorMessage?: string;
    controlReadOnly?: boolean;
    readOnlyTooltip?: string;
}

interface PolicyTagInputRowState {
    tags: string[];
    inputValue: string;
    hasBeenFocused: boolean;
}

class PolicyTagInputRow extends Component<PolicyTagInputRowProps, PolicyTagInputRowState> {
    private readonly nameSpace: string;

    constructor(props: PolicyTagInputRowProps) {
        super(props);
        this.state = {
            tags: props.settingValue,
            inputValue: '',
            hasBeenFocused: false,
        };
        this.nameSpace = `policyEditor.tagsInput.${props.listType}`;
    }

    componentDidUpdate(prevProps: Readonly<PolicyTagInputRowProps>): void {
        const { settingValue } = this.props;
        if (settingValue !== prevProps.settingValue) {
            // eslint-disable-next-line react/no-did-update-set-state
            this.setState({ tags: settingValue });
        }
    }

    onInput = (event: ChangeEvent<HTMLInputElement>): void => {
        const inputValue = event.target.value;

        this.setState({ inputValue });
    }

    onFocus = (): void => {
        this.setState({ hasBeenFocused: true });
    }

    onKeyDown = (event: React.KeyboardEvent<HTMLInputElement>): void => {
        if (this.props.controlReadOnly) {
            return;
        }
        if (event.key === 'Enter') {
            event.preventDefault();
            this.addNew();
        }
    }

    addNew = (): void => {
        if (this.props.controlReadOnly) {
            return;
        }
        const { nameSpace } = this;
        const { inputValue } = this.state;
        const { settingId, handleChange, listType } = this.props;
        const user = inputValue.toLowerCase();
        const isValid = user.match(OPTIONS[listType].validationRegexp);
        if (!isValid) {
            message.error(i18n.t(`${nameSpace}.notValid`));
        } else {
            const { tags } = this.state;

            const tagExist: boolean = tags.includes(user);

            if (tagExist) {
                message.warning(i18n.t(`${nameSpace}.alreadyIncluded`));
            } else {
                const newTagsList = [user, ...tags];
                this.setState({ tags: newTagsList, inputValue: '', hasBeenFocused: false });
                handleChange(settingId, newTagsList);
            }
        }
    }

    onDelete = (tag: string): void => {
        if (this.props.controlReadOnly) {
            return;
        }
        const { tags } = this.state;
        const { settingId, handleChange } = this.props;
        const newTags: string[] = tags.filter((tagValue) => tagValue !== tag);

        this.setState({ tags: newTags });

        handleChange(settingId, newTags);
    }

    render(): JSX.Element {
        const {
            settingId,
            isChild,
            listType,
            hasError = false,
            errorMessage,
            controlReadOnly = false,
            readOnlyTooltip,
        } = this.props;
        const { showDescription = false } = OPTIONS[listType];
        const { tags, inputValue, hasBeenFocused } = this.state;
        const hasTags = tags && !!tags.length;

        // Show error if validation failed AND (user has focused the input OR tried to save)
        const shouldShowError = hasError && hasBeenFocused;

        const inputClasses = classNames('add-tags', {
            'has-error': shouldShowError && !controlReadOnly,
        });

        const inputControl = controlReadOnly ? (
            <Input
                disabled
                onChange={this.onInput}
                onFocus={this.onFocus}
                onKeyDown={this.onKeyDown}
                value={inputValue}
                className={inputClasses}
                placeholder={i18n.t(`${this.nameSpace}.placeholder`)}
                addonAfter={(
                    <Button type="link" disabled onClick={this.addNew}>
                        <PlusOutlined />
                    </Button>
                )}
            />
        ) : (
            <Tooltip
                color="white"
                open={shouldShowError}
                title={errorMessage && (
                    <span className="error-hint">
                        {errorMessage}
                    </span>
                )}
            >
                <Input
                    onChange={this.onInput}
                    onFocus={this.onFocus}
                    onKeyDown={this.onKeyDown}
                    value={inputValue}
                    className={inputClasses}
                    placeholder={i18n.t(`${this.nameSpace}.placeholder`)}
                    addonAfter={(
                        <Button
                            type="link"
                            onClick={this.addNew}
                        >
                            <PlusOutlined />
                        </Button>
                    )}
                />
            </Tooltip>
        );

        const inner = (
            <div className="tags-input-wrapper">
                <PolicySetting
                    isChild={isChild}
                    settingId={settingId}
                    type="tag"
                    hideDescription={!showDescription}
                >
                    {inputControl}
                </PolicySetting>
                {hasTags && (
                    <TagsList
                        readOnly={controlReadOnly}
                        onDelete={this.onDelete}
                        tagsList={tags}
                    />
                )}
            </div>
        );

        if (controlReadOnly && readOnlyTooltip) {
            return <Tooltip title={readOnlyTooltip}>{inner}</Tooltip>;
        }

        return inner;
    }
}

export default PolicyTagInputRow;
